import * as React from 'react';
import { Translation } from 'react-i18next';
import { useMutation } from 'react-query';

import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS, PAYMENTS_BACKEND_URL } from '@/config';
import axios from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { WRONG_CIF, WRONG_DATA_COUNTRIES } from '..';
import { BillingDetails } from '../types';

export type UpdateBillingDetails = {
  data: iFormUserAccountFields;
  groupId: string;
};

export const updateBillingDetails = ({
  data,
  groupId,
}: UpdateBillingDetails): Promise<BillingDetails> => {
  const endPoint = PAYMENTS_BACKEND_URL + 'billing/details/' + groupId;
  return axios
    .put(endPoint, data, {
      validateStatus: (status) => status < 500,
    })
    .then((response: any) => {
      if (typeof response === 'string') {
        throw Error(`Critical error fetching data in ${endPoint}`);
      }

      if (response?.status === 406) {
        if (response?.detail === 'No match') {
          return {
            groupId: groupId,
            hasError: WRONG_DATA_COUNTRIES,
          };
        }

        return {
          groupId: groupId,
          hasError: WRONG_CIF,
        };
      }

      return response;
    })
    .catch((error) => {
      throw Error(`Critical error fetching data in ${endPoint}: ${error}`);
    });
};

type UseUpdateDetailsOptions = {
  config?: MutationConfig<typeof updateBillingDetails>;
};

export const useUpdateBillingDetails = ({ config }: UseUpdateDetailsOptions = {}) => {
  const { addNotification } = useNotificationStore();

  const addNotificationWarning = (hasError: number) => {
    addNotification({
      type: 'warning',
      title: <Translation>{(t) => t('Forms.Checkout.warning.billingDataWarning')}</Translation>,
      message: (
        <Translation>
          {(t) => {
            return hasError === WRONG_CIF
              ? t('Forms.Checkout.warning.billingDataErrorCif')
              : t('Forms.Checkout.warning.billingDataErrorCountries');
          }}
        </Translation>
      ),
      autoClose: NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS,
    });
  };

  return useMutation({
    onMutate: async (updatingDetails: any) => {
      await queryClient.cancelQueries(['details', updatingDetails?.groupId]);

      const previousDetails = queryClient.getQueryData<BillingDetails>([
        'details',
        updatingDetails?.groupId,
      ]);

      queryClient.setQueryData(['details', updatingDetails?.groupId], {
        ...previousDetails,
        ...updatingDetails.data,
        id: updatingDetails.groupId,
      });

      return { previousDetails: previousDetails };
    },
    onError: (_, __, context: any) => {
      if (context?.previousDetails) {
        queryClient.setQueryData(['details', context.previousDetails.id], context.previousDetails);
      }
    },
    onSuccess: (data) => {
      queryClient.refetchQueries(['details', data.id]);
      if (data.hasError) {
        addNotificationWarning(data.hasError);
        return;
      }

      addNotification({
        type: 'success',
        title: <Translation>{(t) => t('Forms.Checkout.success.billingDataSuccess')}</Translation>,
        message: (
          <Translation>
            {(t) => {
              return data.createdAt
                ? t('Forms.Checkout.success.billingDataSuccessDisclaimer')
                : t('Forms.Checkout.success.billingDataSuccessDisclaimerFistTime');
            }}
          </Translation>
        ),
        autoClose: NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS,
      });
    },
    ...config,
    mutationFn: updateBillingDetails,
  });
};
